/* ==========================================================================
   LYVORA V1 — product.js
   Web Components produit. Chargé APRÈS global.js : réutilise window.Lyvora
   (L.Cart, L.Toast, L.formatMoney, L.strings). Zéro dépendance externe.
   Aucun custom element de global.js n'est redéfini ici.
   ========================================================================== */
(function () {
  'use strict';

  const L = window.Lyvora || { routes: {}, settings: {}, strings: {} };
  window.Lyvora = L;

  /* --- Utilitaires ------------------------------------------------------- */
  // Garde-fou : ne jamais redéfinir un élément déjà enregistré
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  // Respecte prefers-reduced-motion pour les défilements programmés
  const scrollBehavior = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  /* --- <lv-variant-picker> ----------------------------------------------------- */
  class LvVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.variants = this.readVariants();
      this.form = document.getElementById(this.dataset.formId || '');
      // Périmètre des mises à jour : la section produit qui contient le picker
      this.root =
        (this.sectionId && this.closest(`#shopify-section-${this.sectionId}`)) ||
        this.closest('.lv-section') ||
        document;

      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[type="radio"][data-option-position], select[data-option-position]');
        if (!input) return;
        this.onOptionChange();
      });
      this.syncActiveClasses();
    }

    readVariants() {
      const script = this.querySelector('script[type="application/json"][data-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    // Valeurs choisies, indexées par position d'option (0-based)
    selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-option-position]').forEach((el) => {
        const pos = parseInt(el.dataset.optionPosition, 10) - 1;
        if (isNaN(pos) || pos < 0) return;
        if (el.matches('select')) values[pos] = el.value;
        else if (el.matches('input[type="radio"]') && el.checked) values[pos] = el.value;
      });
      return values;
    }

    findVariant(values) {
      return this.variants.find((v) =>
        (v.options || []).every((opt, i) => values[i] === undefined || opt === values[i])
      );
    }

    onOptionChange() {
      const variant = this.findVariant(this.selectedOptions()) || null;
      this.syncActiveClasses();
      this.update(variant);
      document.dispatchEvent(
        new CustomEvent('lyvora:variant:change', { detail: { variant, sectionId: this.sectionId } })
      );
    }

    update(variant) {
      // Input caché [name="id"] du formulaire
      const idInput =
        (this.form && (this.form.querySelector('input[name="id"]') || document.querySelector(`input[name="id"][form="${this.form.id}"]`))) ||
        this.querySelector('input[name="id"]');
      if (variant && idInput) idInput.value = variant.id;

      // Prix + prix barré
      this.root.querySelectorAll('[data-price]').forEach((el) => {
        if (variant) el.textContent = L.formatMoney(variant.price);
      });
      this.root.querySelectorAll('[data-compare-price]').forEach((el) => {
        const onSale = variant && variant.compare_at_price && variant.compare_at_price > variant.price;
        el.toggleAttribute('hidden', !onSale);
        if (onSale) el.textContent = L.formatMoney(variant.compare_at_price);
      });

      // Référence (SKU)
      this.root.querySelectorAll('[data-sku]').forEach((el) => {
        const sku = (variant && variant.sku) || '';
        el.textContent = sku;
        el.toggleAttribute('hidden', !sku);
      });

      // Prix dans le bouton d'ajout : mis à jour sauf si des paliers de
      // quantité pilotent ce montant (lv-qty-breaks écoute le changement de variante).
      const hasQtyBreaks = !!(
        this.root.querySelector('lv-qty-breaks') ||
        (this.form && this.form.id && document.querySelector(`lv-qty-breaks[data-form-id="${this.form.id}"]`))
      );
      if (variant && !hasQtyBreaks) {
        this.root.querySelectorAll('[data-atc-price]').forEach((el) => {
          el.textContent = L.formatMoney(variant.price);
        });
      }

      const buttons = new Set(this.root.querySelectorAll('[data-atc]'));
      if (this.form) this.form.querySelectorAll('[data-atc]').forEach((b) => buttons.add(b));
      buttons.forEach((btn) => {
        const label = btn.querySelector('[data-atc-label]');
        const available = !!(variant && variant.available);
        btn.disabled = !available;
        if (!label) return;
        if (!variant) label.textContent = L.strings.unavailable || 'Indisponible';
        else if (!available) label.textContent = L.strings.soldOut || 'Épuisé';
        else label.textContent = L.strings.addToCart || 'Ajouter au panier';
      });

      // URL (?variant=) — jamais depuis la modale quick-add
      if (variant && !this.closest('lv-modal')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    // Classe is-active sur les pills / swatches (label parent ou label[for])
    syncActiveClasses() {
      this.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
        const pill =
          input.closest('label') || (input.id && this.querySelector(`label[for="${input.id}"]`));
        if (pill) pill.classList.toggle('is-active', input.checked);
      });
    }
  }
  define('lv-variant-picker', LvVariantPicker);

  /* --- <lv-product-gallery> ------------------------------------------------------- */
  class LvProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      if (!this.main) return;
      this.sectionId = this.dataset.sectionId || '';

      // Clic vignette → défilement vers le média associé
      if (this.thumbs) {
        this.thumbs.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-thumb]');
          if (!btn) return;
          this.goTo(btn.dataset.mediaId);
        });
      }

      // Changement de variante → média mis en avant
      this.onVariantChange = (e) => {
        const detail = e.detail || {};
        if (this.sectionId && detail.sectionId && detail.sectionId !== this.sectionId) return;
        const mediaId = detail.variant && detail.variant.featured_media && detail.variant.featured_media.id;
        if (mediaId) this.goTo(mediaId);
      };
      document.addEventListener('lyvora:variant:change', this.onVariantChange);

      // Vignette active synchronisée pendant le scroll du conteneur
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActiveThumb(entry.target.dataset.mediaId);
            });
          },
          { root: this.main, threshold: 0.6 }
        );
        this.items().forEach((item) => this.io.observe(item));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('lyvora:variant:change', this.onVariantChange);
      if (this.io) this.io.disconnect();
    }

    items() {
      return [...this.main.querySelectorAll('[data-media-id]')];
    }

    goTo(mediaId) {
      if (!mediaId) return;
      const item = this.items().find((el) => el.dataset.mediaId === String(mediaId));
      if (!item) return;
      item.scrollIntoView({ behavior: scrollBehavior(), inline: 'center', block: 'nearest' });
      this.setActiveThumb(mediaId);
    }

    setActiveThumb(mediaId) {
      if (!this.thumbs || !mediaId) return;
      this.thumbs.querySelectorAll('[data-thumb]').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.mediaId === String(mediaId));
      });
    }
  }
  define('lv-product-gallery', LvProductGallery);

  /* --- <lv-sticky-atc> ----------------------------------------------------------------- */
  class LvStickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById('MainProductAtc');
      if (target && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          // Visible uniquement quand le bouton principal est sorti par le haut
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
        });
        this.io.observe(target);
      }

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sticky-submit]');
        if (!btn) return;
        const form = document.getElementById(btn.dataset.formId || this.dataset.formId || '');
        if (!form) return;
        e.preventDefault();
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }
  }
  define('lv-sticky-atc', LvStickyAtc);

  /* --- <lv-quick-add> ---------------------------------------------------------------------- */
  const quickAddCache = new Map(); // HTML par URL produit

  class LvQuickAdd extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        const url = this.dataset.productUrl;
        if (!url) return;
        e.preventDefault();
        e.stopPropagation(); // la carte produit est cliquable
        if (this.loading) return;

        const modal = document.getElementById('QuickAddModal');
        const slot = modal && modal.querySelector('[data-quick-add-slot]');
        if (!modal || !slot || typeof modal.open !== 'function') {
          window.location = url;
          return;
        }

        const btn = e.target.closest('button') || this.querySelector('button') || this;
        this.loading = true;
        btn.classList.add('is-loading');
        if (!btn.querySelector('.lv-spinner')) {
          const sp = document.createElement('span');
          sp.className = 'lv-spinner';
          btn.appendChild(sp);
        }
        try {
          const html = await this.fetchContent(url);
          if (!html) {
            window.location = url;
            return;
          }
          slot.innerHTML = html;
          modal.open();
        } catch (err) {
          window.location = url; // repli : page produit
        } finally {
          this.loading = false;
          btn.classList.remove('is-loading');
        }
      });
    }

    async fetchContent(url) {
      if (quickAddCache.has(url)) return quickAddCache.get(url);
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', 'quick-add');
      const res = await fetch(fetchUrl.toString());
      if (!res.ok) return '';
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const content = doc.querySelector('[data-quick-add-content]');
      const html = content ? content.innerHTML : '';
      if (html) quickAddCache.set(url, html);
      return html;
    }
  }
  define('lv-quick-add', LvQuickAdd);

  /* --- <lv-delivery-estimate> ------------------------------------------------------------------- */
  class LvDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min || '2', 10);
      const max = parseInt(this.dataset.max || '4', 10);
      const cutoff = parseInt(this.dataset.cutoff || '14', 10);
      const skipWeekends = this.hasAttribute('data-skip-weekends');

      // Commande après l'heure limite → départ le lendemain
      const start = new Date();
      if (start.getHours() >= cutoff) start.setDate(start.getDate() + 1);

      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'fr-FR', { day: 'numeric', month: 'long' });
      const fromEl = this.querySelector('[data-delivery-from]');
      const toEl = this.querySelector('[data-delivery-to]');
      if (fromEl) fromEl.textContent = fmt.format(this.addDays(start, min, skipWeekends));
      if (toEl) toEl.textContent = fmt.format(this.addDays(start, max, skipWeekends));
    }

    // Ajoute `days` jours ; les samedis/dimanches ne comptent pas si skipWeekends
    addDays(from, days, skipWeekends) {
      const date = new Date(from);
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (skipWeekends && (date.getDay() === 0 || date.getDay() === 6)) continue;
        added++;
      }
      return date;
    }
  }
  define('lv-delivery-estimate', LvDeliveryEstimate);

  /* --- <lv-qty-breaks> ------------------------------------------------------------------------------ */
  class LvQtyBreaks extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId || '');
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[type="radio"][data-qty]');
        if (!input) return;
        this.apply(input, false);
      });
      // Changement de variante : recalcule les totaux des paliers (via data-pct).
      this.onVariantChange = (e) => {
        const variant = e.detail && e.detail.variant;
        if (!variant || !variant.price) return;
        this.querySelectorAll('input[type="radio"][data-qty]').forEach((input) => {
          const qty = parseInt(input.dataset.qty || '1', 10);
          const pct = parseFloat(input.dataset.pct || '0');
          const total = Math.round((variant.price * qty * (100 - pct)) / 100);
          input.dataset.totalCents = String(total);
          const label = input.closest('.lv-qty-break');
          const totalEl = label && label.querySelector('[data-break-total]');
          if (totalEl) totalEl.textContent = L.formatMoney(total);
        });
        const checkedNow = this.querySelector('input[type="radio"][data-qty]:checked');
        if (checkedNow) this.apply(checkedNow, true);
      };
      document.addEventListener('lyvora:variant:change', this.onVariantChange);

      // État initial : palier pré-coché par le Liquid
      const checked = this.querySelector('input[type="radio"][data-qty]:checked');
      if (checked) this.apply(checked, true);
    }

    disconnectedCallback() {
      document.removeEventListener('lyvora:variant:change', this.onVariantChange);
    }

    apply(input, initial) {
      // Surbrillance du palier choisi
      this.querySelectorAll('.lv-qty-break').forEach((label) => {
        label.classList.toggle('is-selected', label.contains(input));
      });

      // Quantité du formulaire
      const qty = parseInt(input.dataset.qty || '1', 10);
      const qtyInput = this.form
        ? this.form.querySelector('[name="quantity"]') ||
          document.querySelector(`[name="quantity"][form="${this.form.id}"]`)
        : null;
      if (qtyInput) {
        qtyInput.value = qty;
        if (!initial) qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Prix total affiché sur le bouton d'ajout
      const total = parseInt(input.dataset.totalCents || '', 10);
      if (isNaN(total)) return;
      const scopes = [this.form, this.closest('.lv-section')].filter(Boolean);
      const seen = new Set();
      (scopes.length ? scopes : [document]).forEach((scope) => {
        scope.querySelectorAll('[data-atc-price]').forEach((el) => {
          if (seen.has(el)) return;
          seen.add(el);
          el.textContent = L.formatMoney(total);
        });
      });
    }
  }
  define('lv-qty-breaks', LvQtyBreaks);

  /* --- <lv-bundle> : « Souvent achetés ensemble » --------------------------------------------------- */
  class LvBundle extends HTMLElement {
    connectedCallback() {
      this.base = parseInt(this.dataset.basePrice || '0', 10) || 0;
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.baseEl = this.querySelector('[data-bundle-base]');

      // Compagnons cochés / décochés (délégation)
      this.addEventListener('change', (e) => {
        if (!e.target.closest('input[type="checkbox"][data-addon-price]')) return;
        this.update();
      });

      // Changement de variante du produit principal → nouveau prix de base
      this.onVariantChange = (e) => {
        const detail = e.detail || {};
        const variant = detail.variant;
        if (!variant || variant.price == null) return;
        // L'événement doit concerner la section de ce bloc
        if (detail.sectionId && this.dataset.sectionId) {
          if (detail.sectionId !== this.dataset.sectionId) return;
        } else {
          const root = this.closest('.lv-section') || document;
          if (!root.querySelector('lv-variant-picker')) return;
        }
        this.base = variant.price;
        if (this.baseEl) this.baseEl.textContent = L.formatMoney(variant.price);
        this.update();
      };
      document.addEventListener('lyvora:variant:change', this.onVariantChange);

      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('lyvora:variant:change', this.onVariantChange);
    }

    update() {
      let total = this.base;
      this.querySelectorAll('input[data-addon-price]:checked:not(:disabled)').forEach((input) => {
        total += parseInt(input.dataset.addonPrice || '0', 10) || 0;
      });
      if (this.totalEl) this.totalEl.textContent = L.formatMoney(total);
    }
  }
  define('lv-bundle', LvBundle);

  /* --- <lv-share-button> --------------------------------------------------------------------------------- */
  class LvShareButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        e.preventDefault();
        const title = this.dataset.title || document.title;
        const url = this.dataset.url || window.location.href;

        // Partage natif si disponible (mobile), sinon copie du lien
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (err) {
            /* partage annulé par l'utilisateur */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          if (L.Toast) L.Toast.show('Lien copié !', 'success');
        } catch (err) {
          if (L.Toast) L.Toast.show(L.strings.error || 'Une erreur est survenue.', 'error');
        }
      });
    }
  }
  define('lv-share-button', LvShareButton);

  /* --- <lv-product-zoom-trigger> ----------------------------------------------------------------------------- */
  class LvProductZoomTrigger extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const modal = document.getElementById('ProductZoomModal');
        if (!modal || typeof modal.open !== 'function') return;
        e.preventDefault();
        // Les images de la modale sont en lazy : on force leur chargement à la
        // première ouverture, sinon le panneau s'affiche vide quelques secondes.
        modal.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
        modal.open();
        const id = this.dataset.mediaId;
        if (!id) return;
        // Après ouverture, cale la modale sur le média cliqué
        requestAnimationFrame(() => {
          const target = modal.querySelector(`[data-zoom-media-id="${id}"]`);
          if (target) target.scrollIntoView({ block: 'center' });
        });
      });
    }
  }
  define('lv-product-zoom-trigger', LvProductZoomTrigger);
})();
